<template>
  <div class="livecontrol">
    <h2>Live Control — IPSW</h2>

    <div class="status-row">
      <span class="conn" :class="state.connected.value ? 'conn-ok' : 'conn-bad'">
        Broker: {{ state.connected.value ? 'connected' : 'disconnected' }}
      </span>
      <span v-if="state.lastError.value" class="err">{{ state.lastError.value }}</span>
    </div>

    <section class="card">
      <div class="row">
        <div class="label">Service mode</div>
        <ModeBadge :mode="state.serviceMode.value" />
        <div class="actions">
          <button
            class="btn btn-live"
            :disabled="state.serviceMode.value === 'live'"
            @click="state.setServiceMode('live')"
          >Live</button>
          <button
            class="btn btn-standby"
            :disabled="state.serviceMode.value === 'standby'"
            @click="state.setServiceMode('standby')"
          >Standby</button>
        </div>
      </div>
    </section>

    <section class="card">
      <h3>Vehicles ({{ state.vehicles.value.length }})</h3>
      <table v-if="state.vehicles.value.length" class="veh-table">
        <thead>
          <tr><th>Vehicle</th><th>Mode</th><th>Actions</th></tr>
        </thead>
        <tbody>
          <tr v-for="v in state.vehicles.value" :key="v.id">
            <td class="vid">{{ v.id }}</td>
            <td><ModeBadge :mode="v.mode" /></td>
            <td>
              <button
                class="btn btn-live"
                :disabled="v.mode === 'live'"
                @click="state.setVehicleMode(v.id, 'live')"
              >Live</button>
              <button
                class="btn btn-standby"
                :disabled="v.mode === 'standby'"
                @click="state.setVehicleMode(v.id, 'standby')"
              >Standby</button>
              <button
                class="btn btn-clear"
                :disabled="v.mode === null"
                title="Clear override — vehicle will follow the service mode"
                @click="state.clearVehicleMode(v.id)"
              >Clear</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="empty">No vehicles reporting on <code>public/service/bods/vm/IPSW/+/track</code>.</p>
    </section>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, h } from 'vue';
import State_LiveControlData from '@/statedata/livecontroldata';

const state = new State_LiveControlData();

const ModeBadge = {
  props: { mode: { type: String, default: null } },
  setup(props) {
    return () => {
      const m = props.mode;
      const cls = m === 'live' ? 'badge badge-live'
                : m === 'standby' ? 'badge badge-standby'
                : 'badge badge-unknown';
      const text = m ?? 'unknown';
      return h('span', { class: cls }, text);
    };
  }
};

onMounted(() => state.startPolling(3000));
onUnmounted(() => state.stopPolling());
</script>

<style scoped>
.livecontrol {
  padding: 16px;
  font-family: sans-serif;
}

.status-row {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;
  font-size: 13px;
}

.conn { padding: 2px 8px; border-radius: 3px; font-weight: bold; }
.conn-ok  { background: #d4edda; color: #155724; }
.conn-bad { background: #f8d7da; color: #721c24; }
.err { color: #b00; }

.card {
  background: #f7f7f8;
  border: 1px solid #e1e1e6;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.label { font-weight: bold; min-width: 110px; }

.actions { margin-left: auto; display: flex; gap: 8px; }

.btn {
  border: 1px solid #888;
  background: #fff;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}
.btn:hover:not(:disabled) { background: #eef; }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn-live    { border-color: #1c7c2c; color: #1c7c2c; }
.btn-standby { border-color: #888; color: #444; }
.btn-clear   { border-color: #b86a00; color: #b86a00; }

.badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}
.badge-live    { background: #c8f0cf; color: #15521f; }
.badge-standby { background: #e2e2e6; color: #444; }
.badge-unknown { background: #fff3cd; color: #856404; }

.veh-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}
.veh-table th, .veh-table td {
  text-align: left;
  padding: 6px 10px;
  border-bottom: 1px solid #e5e5ea;
  font-size: 13px;
}
.veh-table th { background: #ececf0; }
.vid { font-family: ui-monospace, Menlo, monospace; }

.empty { color: #666; font-style: italic; }
</style>
